import gql from 'graphql-tag';

// ============================================================================
// FRAGMENTS
// ============================================================================

export const ENDORSEMENT_REQUEST_FRAGMENT = gql`
  fragment EndorsementRequestFields on EndorsementRequest {
    id
    token
    endorserName
    endorserEmail
    endorserInfo
    endorserRelationship
    status
    requestedAt
    expiresAt
    criteria {
      id
      label
    }
    emailSentAt
    emailResendCount
    endorsement {
      criteriaResponses
      observations
      endorserPhone
      completedAt
    }
  }
`;

export const ENDORSEMENT_DASHBOARD_ITEM_FRAGMENT = gql`
  fragment EndorsementDashboardItemFields on EndorsementDashboardItem {
    studentId
    studentName
    programId
    programName
    organizationName
    adkIndex
    fieldIndex
    request {
      ...EndorsementRequestFields
    }
  }
  ${ENDORSEMENT_REQUEST_FRAGMENT}
`;

// ============================================================================
// MUTATIONS - Participant (Authenticated)
// ============================================================================

/**
 * Create an endorsement request
 * Called by participant to request endorsement from someone
 */
export const CREATE_ENDORSEMENT_REQUEST = gql`
  mutation CreateEndorsementRequest($input: CreateEndorsementRequestInput!) {
    createEndorsementRequest(input: $input) {
      success
      token
      requestId
      message
    }
  }
`;

/**
 * Resend endorsement request email
 * Called by participant to resend email to endorser
 */
export const RESEND_ENDORSEMENT_EMAIL = gql`
  mutation ResendEndorsementEmail($input: ResendEndorsementEmailInput!) {
    resendEndorsementEmail(input: $input) {
      success
      message
    }
  }
`;

// ============================================================================
// MUTATIONS - Endorser (Public, token-based)
// ============================================================================

/**
 * Submit an endorsement
 * Called by endorser to complete an endorsement request
 */
export const SUBMIT_ENDORSEMENT = gql`
  mutation SubmitEndorsement($input: SubmitEndorsementInput!) {
    submitEndorsement(input: $input) {
      success
      message
    }
  }
`;

// ============================================================================
// QUERIES - Endorser (Public, token-based)
// ============================================================================

/**
 * Get endorsement dashboard by token
 * Returns all pending endorsements for the email associated with the token
 */
export const GET_ENDORSEMENT_DASHBOARD = gql`
  query EndorsementDashboard($token: String!) {
    endorsementDashboard(token: $token) {
      success
      message
      endorserEmail
      pendingEndorsements {
        studentContext {
          studentId
          studentName
          studentSchool
          programId
          programName
          adkIndex
          fieldIndex
        }
        endorsementRequest {
          id
          token
          endorserName
          endorserEmail
          endorserInfo
          endorserRelationship
          status
          requestedAt
          expiresAt
          criteria {
            id
            label
          }
          emailSentAt
          emailResendCount
          endorsement {
            criteriaResponses
            observations
            endorserPhone
            completedAt
          }
        }
      }
      completedEndorsements {
        studentContext {
          studentId
          studentName
          studentSchool
          programId
          programName
          adkIndex
          fieldIndex
        }
        endorsementRequest {
          id
          token
          endorserName
          endorserEmail
          endorserInfo
          endorserRelationship
          status
          requestedAt
          expiresAt
          criteria {
            id
            label
          }
          emailSentAt
          emailResendCount
          endorsement {
            criteriaResponses
            observations
            endorserPhone
            completedAt
          }
        }
      }
    }
  }
`;

/**
 * Get a single endorsement request by token
 * Used when endorser accesses a specific request directly
 */
export const GET_ENDORSEMENT_BY_TOKEN = gql`
  query EndorsementByToken($token: String!) {
    endorsementByToken(token: $token) {
      endorserEmail
      endorserName
      items {
        ...EndorsementDashboardItemFields
      }
      error
    }
  }
  ${ENDORSEMENT_DASHBOARD_ITEM_FRAGMENT}
`;

/**
 * Get participant report data for endorser review
 * Returns report fields from the ProgramSchema's report template
 */
export const GET_PARTICIPANT_REPORT_FOR_ENDORSER = gql`
  query ParticipantReportForEndorser($token: String!, $studentId: String!, $programId: String!) {
    participantReportForEndorser(token: $token, studentId: $studentId, programId: $programId) {
      success
      message
      studentName
      programName
      templateName
      fields {
        id
        label
        value
        dataType
        isLink
        activityName
      }
    }
  }
`;

// ============================================================================
// QUERIES - Endorser (Authenticated, email-based)
// ============================================================================

/**
 * Get endorsement dashboard by verified email
 * For logged-in endorsers who want to see their pending endorsements
 */
export const GET_ENDORSEMENT_DASHBOARD_BY_EMAIL = gql`
  query EndorsementDashboardByEmail {
    endorsementDashboardByEmail {
      endorserEmail
      endorserName
      items {
        ...EndorsementDashboardItemFields
      }
      error
    }
  }
  ${ENDORSEMENT_DASHBOARD_ITEM_FRAGMENT}
`;

// ============================================================================
// TYPE DEFINITIONS (for TypeScript)
// ============================================================================

export interface EndorsementCriterion {
  id: string;
  label: string;
}

export interface EndorsementData {
  criteriaResponses: Record<string, boolean>;
  observations: string;
  endorserPhone?: string;
  completedAt: string;
}

export interface EndorsementRequest {
  id: string;
  token: string;
  endorserName: string;
  endorserEmail: string;
  endorserInfo?: string;
  endorserRelationship?: string;
  status: 'pending' | 'completed' | 'expired';
  requestedAt: string;
  expiresAt: string;
  criteria: EndorsementCriterion[];
  emailSentAt?: string;
  emailResendCount: number;
  endorsement?: EndorsementData;
}

export interface EndorsementDashboardItem {
  studentId: string;
  studentName: string;
  programId: string;
  programName: string;
  organizationName?: string;
  adkIndex: number;
  fieldIndex: number;
  request: EndorsementRequest;
}

export interface EndorsementDashboardResponse {
  endorserEmail: string;
  endorserName: string;
  items: EndorsementDashboardItem[];
  error?: string;
}

// Input types
export interface CreateEndorsementRequestInput {
  studentId: string;
  programId: string;
  adkIndex: number;
  fieldIndex: number;
  endorserName: string;
  endorserEmail: string;
  endorserInfo?: string;
  endorserRelationship?: string;
  criteria: EndorsementCriterion[];
  customEmailMessage?: string;
  expirationDays: number;
}

export interface ResendEndorsementEmailInput {
  studentId: string;
  programId: string;
  adkIndex: number;
  fieldIndex: number;
  requestId: string;
}

export interface SubmitEndorsementInput {
  token: string;
  studentId: string;
  programId: string;
  adkIndex: number;
  fieldIndex: number;
  requestId: string;
  criteriaResponses: Record<string, boolean>;
  observations: string;
  endorserPhone?: string;
}

// Response types
export interface CreateEndorsementRequestResponse {
  success: boolean;
  token?: string;
  requestId?: string;
  message?: string;
}

export interface ResendEndorsementEmailResponse {
  success: boolean;
  message?: string;
}

export interface SubmitEndorsementResponse {
  success: boolean;
  message?: string;
}

// Participant Report types for endorser review
export interface ParticipantReportField {
  id: string;
  label: string;
  value: unknown;
  dataType: string;
  isLink: boolean;
  activityName?: string;
}

export interface ParticipantReportResponse {
  success: boolean;
  message?: string;
  studentName?: string;
  programName?: string;
  templateName?: string;
  fields?: ParticipantReportField[];
}
